import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import i18next from 'i18next';
import classNames from 'classnames';
import getConfig from 'next/config';

import {
  package as PackagePropType,
} from 'lib/CustomPropTypes';
import { TVE_CANONICAL_ROUTE, ACCESS_ENABLER_OBJ } from 'lib/tve';

import { loadScheduleAction, setAuthenticationAction, showProviderSelectAction } from 'redux/modules/tve';
import { schedule as schedulePropType } from 'lib/CustomPropTypes/TVE';

import ButtonHoverAnimation from 'components/ButtonHoverAnimation';
import IntObserver from 'components/IntObserver';
import Player from 'components/TVE/Player';
import TempPass from 'components/TVE/TempPass';
import WithTVEAuthProvider from 'components/TVE/AuthProvider';
import { ERROR_TYPES } from 'components/TVE/Slate';

import Countdown from './Countdown';
import SignIn from './SignIn';
import PackageTitle from './PackageTitle';
import PlayerInfo from './PlayerInfo';
import Schedule from './Schedule';

class LiveVideoPromo extends React.Component {
  static propTypes = {
    authenticated: PropTypes.bool,
    content: PackagePropType.isRequired,
    epgCurrent: PropTypes.shape({
      guid: PropTypes.string,
      program: PropTypes.shape({
        title: PropTypes.string,
      }),
      startTime: PropTypes.number,
      startTimeFormatted: PropTypes.string,
      thumbMd: PropTypes.string,
    }),
    hasTempPass: PropTypes.bool,
    epgUpcoming: PropTypes.arrayOf(
      PropTypes.shape(schedulePropType),
    ),
    loadSchedule: PropTypes.func,
    providerSelectVisible: PropTypes.bool,
    setAuthentication: PropTypes.func,
    showProviderSelect: PropTypes.func,
    vertical: PropTypes.string,
    videoError: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.bool,
    ]),
  };

  static defaultProps = {
    authenticated: null,
    epgCurrent: {},
    epgUpcoming: [],
    hasTempPass: false,
    loadSchedule: Function.prototype,
    providerSelectVisible: false,
    setAuthentication: Function.prototype,
    showProviderSelect: Function.prototype,
    vertical: null,
    videoError: false,
  };

  constructor(props) {
    super(props);
    this.iframeRef = React.createRef();
    this.state = {
      hasEnteredViewport: false,
    };
  }

  componentDidMount() {
    const { loadSchedule, vertical } = this.props;
    loadSchedule(vertical);
  }

  handleObserverCallback = ([entry]) => {
    const { isIntersecting } = entry;
    const { authenticated, hasTempPass, setAuthentication } = this.props;
    const { hasEnteredViewport } = this.state;
    if (isIntersecting && !hasEnteredViewport) {
      this.setState({ hasEnteredViewport: true });

      if (authenticated === true) return;

      if (
        typeof window !== 'undefined'
        && typeof window[ACCESS_ENABLER_OBJ] !== 'undefined'
        && !hasTempPass
      ) {
        // Reset auth to null to trigger TempPass component update
        setAuthentication({
          authenticated: null,
          hasTempPass: false,
          provider: null,
        });
        setAuthentication({
          authenticated: false,
          hasTempPass: false,
          provider: 'TempPass',
        });
      }
    }
  };

  getAnimatedPlayer = (liveVideoUrl, block) => {
    const {
      epgCurrent,
      showProviderSelect,
    } = this.props;

    const videoSource = epgCurrent && epgCurrent.thumbMd;

    if (!videoSource) return null;

    return (
      <div className={classNames(`${block}__player`, `${block}__player--animated`)}>
        <a href={liveVideoUrl} className={`${block}__player-link`}>
          <video autoPlay loop playsInline muted>
            <source src={videoSource} type="video/mp4" />
          </video>
        </a>
        <button
          aria-label="lock icon"
          type="button"
          onClick={showProviderSelect}
          className="icon icon-lock"
        />
      </div>
    );
  };

  getPlaymakerPlayer = (liveVideoUrl, block) => {
    const { publicRuntimeConfig: { DISABLE_AUTH_TVE_MSNBC } } = getConfig();

    return (
      <>
        <div className={classNames(`${block}__player`, `${block}__player--live`)}>
          <a href={liveVideoUrl} className={`${block}__player-link`}>
            <Player
              autoplayMuted
              bypassAuth={DISABLE_AUTH_TVE_MSNBC}
              externalControlbar={1}
            />
            <span aria-label="volume icon">
              <span className="icon icon-volume-level-0" />
            </span>
          </a>
        </div>
      </>
    );
  };

  render() {
    const {
      authenticated,
      hasTempPass,
      content: {
        id,
        metadata: {
          playmakerButtonLink,
          playmakerButtonText,
          title: packageTitle,
        },
      },
      epgCurrent,
      epgUpcoming,
      providerSelectVisible,
      showProviderSelect,
      videoError,
    } = this.props;

    const { publicRuntimeConfig: { DISABLE_AUTH_TVE_MSNBC } } = getConfig();

    const { hasEnteredViewport } = this.state;
    const showLiveVideo = authenticated || hasTempPass;
    const currentProgramTitle = epgCurrent && epgCurrent.program && epgCurrent.program.title;
    const liveVideoUrl = `${playmakerButtonLink || TVE_CANONICAL_ROUTE}?icid=tve-rail-tease`;
    const upcomingShows = epgUpcoming && epgUpcoming.slice(0, 3);
    const block = 'live-video-promo';

    return (
      <IntObserver
        callback={this.handleObserverCallback}
        threshold={0.25}
      >
        {
          hasEnteredViewport && !DISABLE_AUTH_TVE_MSNBC
            && (
              <>
                <WithTVEAuthProvider iframeRef={this.iframeRef} />
                {videoError !== ERROR_TYPES.BROWSER && (
                  <TempPass />
                )}
              </>
            )
        }
        <section
          data-packageid={id}
          className={block}
        >
          <div className="bacon__header--top-border" data-test="bacon__header__top-border" />
          <div className={`${block}__heading`}>
            <PackageTitle
              block={block}
              text={packageTitle || i18next.t('WATCH MSNBC TV')}
              url={liveVideoUrl}
            />
            {
              hasEnteredViewport
              && !DISABLE_AUTH_TVE_MSNBC
              && (hasTempPass || (!hasTempPass && !authenticated))
              && (
                <div className={`${block}__countdown-sign-in`}>
                  {
                    hasTempPass && <Countdown block={block} />
                  }
                  {
                    !hasTempPass && !authenticated && (
                      <SignIn
                        block={block}
                        providerSelectVisible={providerSelectVisible}
                        showProviderSelect={showProviderSelect}
                      />
                    )
                  }
                </div>
              )
            }
          </div>
          <div className={`${block}__content-wrapper`}>
            <div className={`${block}__player-wrapper`}>
              {
                showLiveVideo
                  ? this.getPlaymakerPlayer(liveVideoUrl, block)
                  : this.getAnimatedPlayer(liveVideoUrl, block)
              }
              <PlayerInfo
                block={block}
                flagText={i18next.t('LIVE')}
                title={currentProgramTitle}
                url={liveVideoUrl}
              />
            </div>
            <div className={`${block}__schedule-action-wrapper`}>
              <Schedule
                block={block}
                items={upcomingShows}
              />
              <ButtonHoverAnimation
                additionalClasses={`${block}__call-to-action`}
                title={playmakerButtonText || i18next.t('WATCH NOW')}
                type="link"
                url={liveVideoUrl}
              />
            </div>
          </div>
        </section>
      </IntObserver>
    );
  }
}

const mapStateToProps = ({ shared, tve, video }) => ({
  authenticated: tve.authenticated,
  epgCurrent: tve.epgCurrent,
  epgUpcoming: tve.epgUpcoming,
  hasTempPass: tve.hasTempPass,
  providerSelectVisible: tve.providerSelectVisible,
  vertical: shared.vertical,
  videoError: video.error,
});

const mapActionsToProps = (dispatch) => ({
  loadSchedule: (vertical) => dispatch(loadScheduleAction(vertical)),
  setAuthentication: (payload) => dispatch(setAuthenticationAction(payload)),
  showProviderSelect: () => dispatch(showProviderSelectAction()),
});

export default connect(mapStateToProps, mapActionsToProps)(LiveVideoPromo);
